<script setup>
import { ref, computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import OrderIssueModal from 'dashboard/routes/dashboard/orderIssues/OrderIssueModal.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';

const props = defineProps({
  issue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');

const showEditModal = ref(false);
const showDeleteModal = ref(false);

const formatDate = dateString => {
  if (!dateString) return '';
  return format(new Date(dateString), 'MMM d, yyyy');
};

const conversationUrl = computed(() => {
  if (!props.issue.conversation?.display_id) return null;
  return `/app/accounts/${currentAccountId.value}/conversations/${props.issue.conversation.display_id}`;
});

// Status display
const statusText = computed(() => {
  if (props.issue.hidden) return t('CONVERSATION_SIDEBAR.ORDER_ISSUES.STATUS_HIDDEN');
  if (props.issue.done) return t('CONVERSATION_SIDEBAR.ORDER_ISSUES.STATUS_DONE');
  return t('CONVERSATION_SIDEBAR.ORDER_ISSUES.STATUS_ACTIVE');
});

const statusClass = computed(() => {
  if (props.issue.hidden) return 'bg-n-amber-3 text-n-amber-11';
  if (props.issue.done) return 'bg-n-slate-3 text-n-slate-11';
  return 'bg-n-teal-3 text-n-teal-11';
});

// Toggle done status
const toggleDone = async () => {
  try {
    if (props.issue.done) {
      await orderIssuesAPI.unarchive(props.issue.id);
      useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.UNARCHIVED'));
    } else {
      await orderIssuesAPI.markAsDone(props.issue.id);
      useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.ARCHIVED'));
    }
    emit('updated');
  } catch (e) {
    useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.UPDATE_ERROR'));
  }
};

// Toggle hidden status
const toggleHidden = async () => {
  try {
    await orderIssuesAPI.toggleHidden(props.issue.id, !props.issue.hidden);
    useAlert(
      props.issue.hidden
        ? t('CONVERSATION_SIDEBAR.ORDER_ISSUES.SHOWN')
        : t('CONVERSATION_SIDEBAR.ORDER_ISSUES.HIDDEN')
    );
    emit('updated');
  } catch (e) {
    useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.UPDATE_ERROR'));
  }
};

// Delete issue
const confirmDelete = async () => {
  try {
    await orderIssuesAPI.delete(props.issue.id);
    useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETED'));
    showDeleteModal.value = false;
    emit('updated');
  } catch (e) {
    useAlert(t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE_ERROR'));
  }
};

const handleEditSuccess = () => {
  showEditModal.value = false;
  emit('updated');
};
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
    :class="{ 'opacity-50': issue.hidden || issue.done }"
  >
    <!-- Row 1: Order Number + Status Badge -->
    <div class="flex justify-between items-center">
      <div class="font-medium flex items-center gap-2">
        <span class="text-n-slate-12" :class="{ 'line-through': issue.done }">
          {{ issue.order_number || 'N/A' }}
        </span>
        <a
          v-if="conversationUrl"
          :href="conversationUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-n-slate-11 hover:text-n-brand hover:underline"
          :title="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.GO_TO_CONVERSATION')"
        >
          #{{ issue.conversation?.display_id }}
          <i class="i-lucide-external-link text-[10px]" />
        </a>
      </div>
      <!-- Status Badge -->
      <div
        :class="statusClass"
        class="text-xs px-2 py-0.5 rounded capitalize"
      >
        {{ statusText }}
      </div>
    </div>

    <!-- Row 2: Tags (left) + Icons (right) -->
    <div class="flex justify-between items-start">
      <!-- Tags (stacked: problem first, then action) -->
      <div v-if="issue.problem_tag || issue.action_tag" class="flex flex-col gap-1">
        <span
          v-if="issue.problem_tag"
          class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-n-ruby-3 text-n-ruby-11"
        >
          {{ issue.problem_tag }}
        </span>
        <span
          v-if="issue.action_tag"
          class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-n-amber-3 text-n-amber-11"
        >
          {{ issue.action_tag }}
        </span>
      </div>
      <div v-else />
      <!-- Action Icons -->
      <div class="flex items-center gap-0.5">
        <!-- Show/Hide -->
        <button
          class="p-1 transition-colors"
          :class="issue.hidden 
            ? 'text-amber-500 hover:text-amber-300' 
            : 'text-n-slate-11 hover:text-n-brand'"
          :title="issue.hidden ? $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.SHOW') : $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.HIDE')"
          @click="toggleHidden"
        >
          <i :class="issue.hidden ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="text-sm" />
        </button>
        <!-- Edit -->
        <button
          class="p-1 text-n-slate-11 hover:text-n-brand transition-colors"
          :title="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.EDIT')"
          @click="showEditModal = true"
        >
          <i class="i-lucide-pencil text-sm" />
        </button>
        <!-- Delete -->
        <button
          class="p-1 text-n-slate-11 hover:text-red-500 transition-colors"
          :title="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE')"
          @click="showDeleteModal = true"
        >
          <i class="i-lucide-trash-2 text-sm" />
        </button>
      </div>
    </div>

    <!-- Note -->
    <div v-if="issue.note" class="text-sm text-n-slate-11">
      {{ issue.note }}
    </div>

    <!-- Footer: Info + Actions -->
    <div class="flex justify-between items-center text-sm">
      <div class="flex flex-col text-xs text-n-slate-11">
        <span v-if="issue.user?.name">{{ issue.user.name }}</span>
        <span>{{ formatDate(issue.created_at) }}</span>
      </div>
      <!-- Mark as Done / Reopen -->
      <button
        class="text-xs px-2 py-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
        @click="toggleDone"
      >
        {{ issue.done ? $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.REOPEN') : $t('CONVERSATION_SIDEBAR.ORDER_ISSUES.MARK_DONE') }}
      </button>
    </div>
  </div>

  <!-- Edit Modal -->
  <OrderIssueModal
    :show="showEditModal"
    :order-issue="issue"
    @close="showEditModal = false"
    @success="handleEditSuccess"
  />

  <!-- Delete Confirmation Modal -->
  <DeleteModal
    v-if="showDeleteModal"
    v-model:show="showDeleteModal"
    :title="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE_TITLE')"
    :message="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE_MESSAGE')"
    :message-value="issue.order_number"
    :confirm-text="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE_CONFIRM')"
    :reject-text="$t('CONVERSATION_SIDEBAR.ORDER_ISSUES.DELETE_CANCEL')"
    :on-confirm="confirmDelete"
    :on-close="() => showDeleteModal = false"
  />
</template>
